import { useEffect, useState } from 'react';
import api from '@/lib/api';
import Seo from '@/components/Seo';
import { toast, Toaster } from 'sonner';
import { Search, ShoppingBag, Tag, ExternalLink, MapPin } from 'lucide-react';

const CATS = [
  { key: '', label: 'All' },
  { key: 'vehicles', label: 'Vehicles' },
  { key: 'tools', label: 'Tools & DIY' },
  { key: 'machinery', label: 'Machinery' },
  { key: 'parts', label: 'Vehicle Parts' },
];
const SORTS = [
  { key: 'best', label: 'Best match' },
  { key: 'price_asc', label: 'Price: low to high' },
  { key: 'price_desc', label: 'Price: high to low' },
  { key: 'newest', label: 'Newly listed' },
];

const gbp = (v, c) => (v == null ? '' : `£${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

export default function MarketplacePage() {
  const [enabled, setEnabled] = useState(true);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState('best');
  const [condition, setCondition] = useState('');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get('/marketplace/status').then((r) => setEnabled(r.data.enabled)).catch(() => {});
  }, []);

  const search = async (e, opts = {}) => {
    e?.preventDefault();
    const query = opts.q ?? q;
    if (!query.trim()) { toast.error('Type what you\u2019re looking for'); return; }
    setLoading(true);
    try {
      const r = await api.get('/marketplace/search', {
        params: { q: query, category: opts.category ?? category, sort: opts.sort ?? sort, condition: opts.condition ?? condition },
      });
      setItems(r.data.items || []);
    } catch (err) {
      const msg = err.response?.status === 503 ? 'Marketplace is coming soon.' : (err.response?.data?.detail || 'Search failed');
      toast.error(typeof msg === 'string' ? msg : 'Search failed');
      setItems(null);
    } finally { setLoading(false); }
  };

  const reSearch = (opts) => { if (items !== null || q.trim()) search(null, opts); };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo title="Trade Marketplace — Vehicles, Tools & Machinery" description="Search vehicles, tools, machinery and parts across eBay UK — best prices in one place for tradesmen." />
      <Toaster position="top-center" richColors />

      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-brand font-semibold">Powered by eBay UK</p>
        <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight mt-1">Trade Marketplace</h1>
        <p className="text-slate-600 mt-2">Search vehicles, tools, machinery &amp; parts — find the best prices on eBay UK, all in one place.</p>
      </div>

      {!enabled && (
        <div className="tm-card p-5 mb-6 bg-amber-50 border-amber-200" data-testid="marketplace-disabled">
          <p className="text-sm text-amber-800"><strong>Nearly there.</strong> The marketplace switches on as soon as the eBay connection is configured.</p>
        </div>
      )}

      <div className="tm-card p-5 mb-6">
        <form onSubmit={search} className="flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className="tm-input pl-9" placeholder="e.g. Makita drill, Transit van, mini digger" value={q}
              onChange={(e) => setQ(e.target.value)} data-testid="marketplace-search-input" />
          </div>
          <button type="submit" className="tm-btn" data-testid="marketplace-search-btn"><Search size={16}/> Search</button>
        </form>

        <div className="flex flex-wrap gap-1 mt-4">
          {CATS.map((c) => (
            <button key={c.key} onClick={() => { setCategory(c.key); reSearch({ category: c.key }); }} data-testid={`marketplace-cat-${c.key || 'all'}`}
              className={`px-3 py-1.5 text-xs font-mono uppercase border ${category === c.key ? 'bg-slate-900 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-800'}`}>
              {c.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-3">
          <select className="tm-input bg-white text-sm w-auto" value={sort} onChange={(e) => { setSort(e.target.value); reSearch({ sort: e.target.value }); }} data-testid="marketplace-sort">
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select className="tm-input bg-white text-sm w-auto" value={condition} onChange={(e) => { setCondition(e.target.value); reSearch({ condition: e.target.value }); }} data-testid="marketplace-condition">
            <option value="">Any condition</option>
            <option value="NEW">New</option>
            <option value="USED">Used</option>
          </select>
        </div>
      </div>

      {loading && <p className="font-mono text-slate-400" data-testid="marketplace-loading">Searching eBay\u2026</p>}

      {!loading && items !== null && (
        items.length === 0 ? (
          <div className="tm-card p-8 text-center text-slate-500">No listings found. Try different keywords.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="marketplace-results">
            {items.map((it, i) => (
              <a key={it.id || i} href={it.url} target="_blank" rel="noreferrer" data-testid={`marketplace-item-${i}`}
                className="tm-card overflow-hidden group flex flex-col hover:shadow-lg transition-shadow">
                <div className="aspect-square bg-slate-100 overflow-hidden">
                  {it.image ? <img src={it.image} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                    : <div className="w-full h-full flex items-center justify-center text-slate-300"><ShoppingBag size={32}/></div>}
                </div>
                <div className="p-3 flex flex-col flex-1">
                  <p className="text-sm font-medium line-clamp-2 flex-1">{it.title}</p>
                  <p className="font-display font-semibold text-xl mt-2">{gbp(it.price)}</p>
                  <div className="flex items-center justify-between mt-1 text-[11px] font-mono text-slate-400">
                    <span className="flex items-center gap-1"><Tag size={11}/> {it.condition || '—'}</span>
                    {it.location && <span className="flex items-center gap-1 truncate"><MapPin size={11}/> {it.location}</span>}
                  </div>
                  <span className="mt-2 text-[11px] font-mono text-brand flex items-center gap-1">View on eBay <ExternalLink size={11}/></span>
                </div>
              </a>
            ))}
          </div>
        )
      )}

      {!loading && items === null && (
        <div className="tm-card p-10 text-center" data-testid="marketplace-empty">
          <ShoppingBag size={40} className="mx-auto text-slate-300 mb-3" />
          <h3 className="font-display font-bold text-lg uppercase">Find your next bit of kit</h3>
          <p className="text-slate-500 mt-1">Search tools, machinery, vans and parts across eBay UK.</p>
        </div>
      )}

      {items !== null && items.length > 0 && (
        <p className="text-[11px] text-slate-400 mt-6">Listings and prices from eBay UK. Prices and availability change frequently — check the listing for full details.</p>
      )}
    </div>
  );
}
